// src/components/assistant/WorkspaceChat.tsx
// Workspace mode chat - optimized for widget generation

import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench } from "lucide-react";
import ChatComposer from "./ChatComposer";
import ChatEmptyState from "./ChatEmptyState";
import WorkspaceWidgetCards from "./WorkspaceWidgetCards";
import { useWorkspaceMode } from "@/hooks/assistant/useWorkspaceMode";
import type { ChatMessage } from "@/services/assistant/AssistantService";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface WorkspaceChatProps {
  prefill?: string | undefined;
  onPrefillUsed?: () => void;
  compact?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Message Row
// ─────────────────────────────────────────────────────────────────────────────

interface MessageRowProps {
  message: ChatMessage;
  showAvatar: boolean;
}

const MessageRow: React.FC<MessageRowProps> = ({ message, showAvatar }) => {
  const isUser = message.role === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      {/* Assistant Avatar */}
      {!isUser && (
        <div className={`mr-2 mt-1 flex-shrink-0 ${showAvatar ? "opacity-100" : "opacity-0"}`}>
          <div className="h-7 w-7 rounded-lg bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30">
            <Wrench className="h-3.5 w-3.5 text-emerald-400" />
          </div>
        </div>
      )}

      {/* Message Content */}
      <div className={`${isUser ? "max-w-[80%]" : "max-w-[90%]"}`}>
        <div
          className={`
            rounded-xl shadow-sm text-sm
            ${isUser
              ? "bg-emerald-500/20 text-white px-3 py-2 border border-emerald-500/30"
              : "bg-gray-800/50 text-gray-100 px-3 py-2"
            }
          `}
        >
          <div className="whitespace-pre-wrap break-words leading-relaxed">
            {message.content}
          </div>
        </div>
      </div>

      {/* User Avatar */}
      {isUser && (
        <div className={`ml-2 mt-1 flex-shrink-0 ${showAvatar ? "opacity-100" : "opacity-0"}`}>
          <div className="h-7 w-7 rounded-lg bg-emerald-500/30 flex items-center justify-center border border-emerald-500/40">
            <span className="text-[10px] font-semibold text-emerald-300">You</span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const WorkspaceChat: React.FC<WorkspaceChatProps> = ({
  prefill,
  onPrefillUsed,
  compact = true,
}) => {
  const [state, actions] = useWorkspaceMode();
  const { messages, isLoading, pendingWidgets } = state;
  const { sendMessage } = actions;

  const [input, setInput] = React.useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, isLoading, pendingWidgets.length]);

  // Handle prefill
  useEffect(() => {
    if (prefill && !isLoading && input.trim().length === 0) {
      setInput(prefill);
      onPrefillUsed?.();
    }
  }, [prefill, isLoading, input, onPrefillUsed]);

  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;
    const content = input.trim();
    setInput("");
    await sendMessage(content);
  };

  // Compute previous roles for avatar visibility
  const previousRoles = messages.map((_, idx) =>
    idx > 0 ? messages[idx - 1]?.role ?? null : null
  );

  const showEmpty = messages.length === 0 && !isLoading;

  // Handle suggestion click - send the message directly
  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    void sendMessage(suggestion);
  };

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Messages Area */}
      <div ref={scrollRef} className={`flex-1 overflow-y-auto ${showEmpty ? "flex flex-col justify-center" : ""}`}>
        <div className={`mx-auto w-full ${compact ? "px-3 pt-16 pb-4" : "max-w-5xl px-4 md:px-6 pt-28 pb-4"}`}>
          <AnimatePresence mode="wait">
            {showEmpty ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ChatEmptyState
                  mode="workspace"
                  onSuggestionClick={handleSuggestionClick}
                />
              </motion.div>
            ) : (
              <motion.div
                key="timeline"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-3"
              >
                {messages.map((msg, idx) => (
                  <MessageRow
                    key={msg.id}
                    message={msg}
                    showAvatar={previousRoles[idx] !== msg.role}
                  />
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-gray-400"
                  >
                    <div className="h-7 w-7 rounded-lg bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30">
                      <Wrench className="h-3.5 w-3.5 text-emerald-400" />
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-800/50">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          className="h-1.5 w-1.5 rounded-full bg-emerald-400"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
                        />
                      ))}
                      <span className="ml-1 text-xs text-emerald-400">Creating widgets...</span>
                    </div>
                  </motion.div>
                )}

                {/* Widget Cards (shown after response) */}
                {pendingWidgets.length > 0 && !isLoading && (
                  <div className="ml-9">
                    <WorkspaceWidgetCards widgets={pendingWidgets} />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Composer */}
      <ChatComposer
        value={input}
        onChange={setInput}
        onSubmit={handleSubmit}
        onStop={() => {}} // Workspace mode doesn't support stop
        isLoading={isLoading}
        mode="workspace"
        onModeChange={() => {}} // Mode is fixed in this component
        placeholder="Describe widgets to create..."
        compact={compact}
      />
    </div>
  );
};

export default WorkspaceChat;
